import { SanityDocument, SanityDocumentStub } from '@sanity/client';
import { Assets } from './assets';
import { Invoices } from './invoices';

export namespace Sanity {
  export namespace Types {
    export enum DocumentTypes {
      ASSET = 'asset',
      ASSET_PACKAGE = 'assetPackage',
      INVOICE = 'invoice',
      LINE_ITEM_FROZEN = 'lineItemFrozen',
      ORDER = 'order',
      ORDER_ITEM = 'orderItem',
      USER = 'user',
    }

    export interface BaseDocument extends SanityDocumentStub {
      _key: string;
    }

    /**
     * @interface CartItemReference
     * @description Sanity reference object used in cart.items array
     * @example { _ref: 'asset-123', _type: 'reference', _key: 'asset.abc' }
     */
    export interface CartItemReference {
      _ref: string;
      _type: string;
      _key: string;
    }

    export enum SanityTone {
      DEFAULT = 'default',
      PRIMARY = 'primary',
      POSITIVE = 'positive',
      CAUTION = 'caution',
      CRITICAL = 'critical',
    }

    export const SanityToneColors = {
      [SanityTone.DEFAULT]: '#8690A0',
      [SanityTone.PRIMARY]: '#82b2fd',
      [SanityTone.POSITIVE]: '#4fd97f',
      [SanityTone.CAUTION]: '#fbd333',
      [SanityTone.CRITICAL]: '#F03E2F',
    };
    export interface Item {
      /** Optional canonical Sanity asset document id for this item */
      assetId?: string;
      assetFilePath?: string;
      assetProvider: Assets.Types.Providers;
      assetSlug: string;
      category: string;
      fileTitle: string;
      filmSlug: string;
      filmTitle: string;
      isFolder?: boolean;
      mediaShuttleFilePath?: string;
      price?: number;
      subCategory: string;
      thumbnail?: string;
      preview?: string;
      _key?: string;
      [key: string]: unknown;
    }

    export interface FreezeItemProps extends Item {
      thumbnail: string;
      preview: string;
    }

    export type FrozenItem<T = OrderItem> = BaseDocument & T;

    export interface OrderItem extends Item {
      thumbnail: FreezeItemProps['thumbnail'] | FreezeItemProps['preview'];
    }

    export interface OrderAssetPackage {
      assetProvider: Assets.Types.Providers;
      link: string;
      id: string;
      fileId: string;
      contentsFileId?: string;
      expiry?: string;
      linkExpiry?: string;
      password?: string;
      linkPassword?: string;
      items: string[];
      /** Transfer UUID returned by /ops/transfers for the asset upload */
      transferId?: string;
      transferStatus?: Assets.Types.TransferStatus;
      transferError?: string;
    }

    export interface OrderInvoice extends BaseDocument {
      id: string | number;
      number?: string;
      customer?: string | number;
      invoiceProvider?: Invoices.Types.Providers;
      status?: string;
      draft?: boolean;
      paid?: boolean;
      closed?: boolean;
      date?: string;
      currency?: string;
      balance?: number;
      purchase_order?: string;
      url?: string;
      payment_url?: string;
      pdf_url?: string;
      created?: string;
      updated?: string;
      metadata?: Record<string, unknown>;
      notes?: string;
    }

    export interface OrderDownloadLegacy {
      downloadLink: string;
      downloadLinkExpiresOn: string;
      downloadTokenId: string;
    }

    export interface Order extends SanityDocument, Partial<OrderDownloadLegacy> {
      items: FrozenItem[];
      orderNumber: string | number;
      packages: OrderAssetPackage[];
      total: number;
      userEmail: string;
      invoices?: OrderInvoice[];
      /**
       * @deprecated Use order.invoices[].invoiceProvider instead
       */
      invoiceProvider?: Invoices.Types.Providers;
      /**
       * @deprecated Use order.invoices[].id instead
       */
      invoiceId?: string | number;
      /**
       * @deprecated Use order.invoices[].number instead
       */
      invoiceNumber?: string | number;
    }

    export interface FrozenOrder extends Order {
      packages: FrozenItem<OrderAssetPackage>[];
      invoices?: OrderInvoice[];
    }

    /**
     * @interface OrderReturn
     * @description Return type for checkout completion operations
     */
    export interface OrderReturn {
      orderSlug: string;
      success: boolean;
      debug?: unknown;
      order?: unknown;
      items?: unknown[];
    }

    export interface WebhookPayload {
      orderId: string;
    }

    /**
     * @interface CartItem
     * @description Cart item from Sanity user document (display mode)
     */
    export interface CartItem {
      _id: string;
      title: string;
      price: number;
      itemCode?: string;
      filmTitle: string;
      filmId: string;
      platformId?: string;
      category: string;
    }

    /**
     * @interface PromoCode
     * @description Promo code applied to cart
     */
    export interface PromoCode {
      id: string;
      name: string;
      code: string;
      percentOff?: number;
      dollarsOff?: number;
      unlimited?: boolean;
    }

    /**
     * @interface Cart
     * @description User cart with items, promo, and invoice info
     * @template T - Cart item type (CartItem for display, Item for orders)
     */
    export interface Cart<T = CartItem> {
      items: T[];
      promo?: PromoCode;
      orderNumber?: number;
      /** @deprecated Legacy Billdu field for backwards compatibility only */
      billduInvoiceNumber?: string | number;
      /**
       * @deprecated Use cart.invoiceData.id instead
       */
      invoiceId?: string | number;
      /**
       * @deprecated Use cart.invoiceData.number instead
       */
      invoiceNumber?: string | number;
      /**
       * @deprecated Use cart.invoiceData.metadata.provider or infer from invoiceData
       */
      invoiceProvider?: Invoices.Types.Providers;
      /** Full invoice object from invoice provider (replaces individual invoice* fields) */
      invoiceData?: Invoices.Types.Invoice;
      /** True when every item in the cart is free by category rules (no invoice required) */
      freeOrder?: boolean;
      newOrder?: string | number;
      total?: number;
      [key: string]: unknown;
    }

    /**
     * @interface FreeCategoryPerTitle
     * @description Free category assignment per film title
     */
    export interface FreeCategoryPerTitle {
      film: string;
      category: string;
    }

    /**
     * @interface Distributor
     * @description Sanity distributor document with invoice provider settings
     */
    export interface Distributor extends SanityDocument {
      _id: string;
      name: string;
      invoiceProvider?: Invoices.Types.Providers;
      invoiceClientId?: number;
      territoryId?: number;
    }

    /**
     * @interface User
     * @description Sanity user document with cart and billing info
     * @template T - Cart item type (CartItem for display, Item for orders)
     */
    export interface User<T = CartItem> extends SanityDocument {
      _id: string;
      billduClientId?: string | number;
      invoiceClientId?: string | number;
      invoiceProvider?: Invoices.Types.Providers;
      email: string;
      territoryId?: number;
      distributor?: Distributor;
      freeCategories?: Array<{ name: string }>;
      freeCategoriesPerTitle?: FreeCategoryPerTitle[];
      cart: Cart<T>;
      [key: string]: unknown;
    }
  }
}
